//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import _ from 'underscore';

import { ICollectionFiltersDto } from '@apis/collections/ICollectionFiltersDto';
import { ICollectionSourcesDto } from '@apis/collections/ICollectionSourcesDto';
import { ICollectionSourcesMetadataDto } from '@apis/collections/ICollectionSourcesMetadataDto';
import TrackedSourceId from '@models/TrackedSourceId';
import { IStreamSource } from '@streams-drawer/model/IStreamSource';
import {
  INSTAGRAM_STREAM_SOURCE_NETWORK,
  StreamSourceTypes,
  StreamExcludedContentSourceTypes,
  TIKTOK_STREAM_SOURCE_NETWORK,
} from '@streams/constants/streamCreationConstants';

export function convertStreamSourcesToSources(
  streamSources: ICollectionSourcesDto,
  sourcesMetadata: ICollectionSourcesMetadataDto,
): IStreamSource[] {
  const baseInstagramHashtagSources = streamSources?.instagramHashtags;
  const baseUserSources = streamSources?.instagramBusinessUsers;
  const mentionsAndTags = streamSources?.instagramMentions;
  const tiktokMentionsAndTags = streamSources?.tiktokMentions;
  const allCuralateUsers = streamSources?.allCuralateUsers;
  const allInstagram = streamSources?.allInstagram;
  const allTikTok = streamSources?.allTikTok;

  const mentions = (mentionsAndTags ?? []).reduce<IStreamSource[]>(
    (acc, mentionId) => {
      const userId = TrackedSourceId(mentionId);
      const username = sourcesMetadata?.instagramMentions[userId]?.username;

      if (username) {
        acc.push({
          sourceNetwork: INSTAGRAM_STREAM_SOURCE_NETWORK,
          sourceType: StreamSourceTypes.MENTIONS_AND_TAGS,
          sourceValue: username,
          sourceLabel: `@${username} Instagram Mentions & Tags`,
        });
      }
      return acc;
    },
    [],
  );

  const tiktokMentions = (tiktokMentionsAndTags ?? []).reduce<IStreamSource[]>(
    (acc, mentionId) => {
      const userId = TrackedSourceId(mentionId);
      const username = sourcesMetadata?.tiktokMentions?.[userId]?.username;

      if (username) {
        acc.push({
          sourceNetwork: TIKTOK_STREAM_SOURCE_NETWORK,
          sourceType: StreamSourceTypes.MENTIONS_AND_TAGS,
          sourceValue: userId,
          sourceLabel: `@${username} TikTok Mentions`,
        });
      }
      return acc;
    },
    [],
  );

  const hashtagSources =
    _(baseInstagramHashtagSources).map((hashtagName) => {
      return {
        sourceNetwork: INSTAGRAM_STREAM_SOURCE_NETWORK,
        sourceType: StreamSourceTypes.HASHTAG,
        sourceValue: hashtagName,
        sourceLabel: `#${hashtagName}`,
      };
    }) || [];

  const userSources =
    _(baseUserSources).map((businessUserId: TrackedSourceId) => {
      const matchingTrackedBusinessUser =
        sourcesMetadata.instagramBusinessUsers[businessUserId];
      const username =
        matchingTrackedBusinessUser && matchingTrackedBusinessUser.username;

      return {
        sourceNetwork: INSTAGRAM_STREAM_SOURCE_NETWORK,
        sourceType: StreamSourceTypes.USER,
        sourceValue: businessUserId,
        sourceLabel: `@${username}`,
      };
    }) || [];

  const uploadedSource = allCuralateUsers
    ? [
        {
          sourceNetwork: '',
          sourceType: StreamSourceTypes.TEAM_MEDIA,
          sourceValue: 'teamMedia',
          sourceLabel: 'Team Media',
        },
      ]
    : [];

  const ugc = allInstagram
    ? [
        {
          sourceNetwork: '',
          sourceType: StreamSourceTypes.UGC,
          sourceValue: 'ugc',
          sourceLabel: 'UGC',
        },
      ]
    : [];

  const tiktokUgc = allTikTok
    ? [
        {
          sourceNetwork: TIKTOK_STREAM_SOURCE_NETWORK,
          sourceType: StreamSourceTypes.UGC,
          sourceValue: 'tiktokUgc',
          sourceLabel: 'TikTok UGC',
        },
      ]
    : [];

  const sourcesList = [
    ...hashtagSources,
    ...userSources,
    ...ugc,
    ...tiktokUgc,
    ...uploadedSource,
  ];
  const sortedHashtagsAndUsersAndUploads = _(sourcesList).sortBy('sourceLabel');

  return [...mentions, ...tiktokMentions, ...sortedHashtagsAndUsersAndUploads];
}

export function convertStreamExcludedSourcesToExcludedSources(
  streamFilters: ICollectionFiltersDto,
) {
  const excludedSources = streamFilters.excludedContentSources;

  return excludedSources?.map((excludedContentSource) => {
    const value = excludedContentSource.value;
    const type =
      excludedContentSource.contentType ===
      StreamExcludedContentSourceTypes.USERNAME
        ? StreamSourceTypes.USER
        : StreamSourceTypes.HASHTAG;
    const label =
      excludedContentSource.contentType ===
      StreamExcludedContentSourceTypes.USERNAME
        ? `@${value}`
        : `#${value}`;

    return {
      sourceLabel: label,
      sourceNetwork: INSTAGRAM_STREAM_SOURCE_NETWORK,
      sourceType: type,
      sourceValue: value,
    };
  });
}
